import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import ConfirmDialog from "./ConfirmDialog";
import {
  downloadSelection,
  getErrorMessage,
  loadJson,
  readApiError,
  TabProps,
  toCountMap,
} from "./lib";
import {
  compressScrapPhoto,
  formatBytes,
  formatTimestamp,
  makeThumbnail,
  ScrapContract,
  ScrapContractDetail,
  ScrapImage,
  ScrapItem,
  SCRAP_MAX_BYTES,
} from "./scrap-photos";
import { CameraScanProvider, ScanField } from "./scan";

const SIZE_LIMIT_KB = Math.round(SCRAP_MAX_BYTES / 1024);

/**
 * Scrap contracts, one photo folder each. A contract is scanned in with just
 * its number, then filled with any number of scanned Item/SN entries — each
 * of those is its own little photo gallery, added to and cleared out on its
 * own. Closing the contract seals every item's photos at once; after that it
 * only offers a download, never another edit (short of reopening it).
 */
export default function ScrapPhotoUploadTab({
  isActive,
  selectedDate,
  rangeFrom,
  rangeTo,
  refreshToken,
  onCounts,
  onSynced,
}: TabProps) {
  const [contracts, setContracts] = useState<ScrapContract[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [newContractNumber, setNewContractNumber] = useState("");
  const [openContractId, setOpenContractId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ScrapContractDetail | null>(null);
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [newItemSn, setNewItemSn] = useState("");
  const [isAddingItemBusy, setIsAddingItemBusy] = useState(false);
  const [selectedImageIds, setSelectedImageIds] = useState<Set<number>>(new Set());
  const [uploadingItemId, setUploadingItemId] = useState<number | null>(null);
  const [uploadFileProgress, setUploadFileProgress] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState("");
  const [isDeletingSelection, setIsDeletingSelection] = useState(false);
  const [confirmDeleteSelection, setConfirmDeleteSelection] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [confirmDeleteContract, setConfirmDeleteContract] = useState<ScrapContract | null>(
    null,
  );
  const [isDeletingContract, setIsDeletingContract] = useState(false);
  const [confirmDeleteItem, setConfirmDeleteItem] = useState<ScrapItem | null>(null);
  const [isDeletingItem, setIsDeletingItem] = useState(false);
  const [reopeningId, setReopeningId] = useState<number | null>(null);
  const [error, setError] = useState("");
  const formRef = useRef<HTMLFormElement>(null);

  const loadCounts = useCallback(async () => {
    const data = await loadJson<{ counts: { date: string; count: number }[] }>(
      `/api/scrap-contracts?from=${rangeFrom}&to=${rangeTo}`,
      "A szerződések betöltése sikertelen.",
    );
    onCounts(toCountMap(data.counts));
  }, [rangeFrom, rangeTo, onCounts]);

  const loadContracts = useCallback(async (date: string) => {
    const data = await loadJson<{ contracts: ScrapContract[] }>(
      `/api/scrap-contracts?date=${date}`,
      "A szerződések betöltése sikertelen.",
    );
    setContracts(data.contracts);
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([loadCounts(), loadContracts(selectedDate)])
      .then(() => {
        if (!cancelled) onSynced(true);
      })
      .catch((loadError) => {
        if (!cancelled) {
          setError(getErrorMessage(loadError, "A szerződések betöltése sikertelen."));
          onSynced(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [loadCounts, loadContracts, selectedDate, refreshToken, onSynced]);

  // A day switch closes whatever contract panel was open — the same as every
  // other tab resetting its form when the selected day changes.
  useEffect(() => {
    setOpenContractId(null);
    setDetail(null);
    setSelectedImageIds(new Set());
    setIsAdding(false);
    setNewContractNumber("");
  }, [selectedDate]);

  const openContract = async (id: number) => {
    setError("");
    setSelectedImageIds(new Set());
    setOpenContractId(id);
    try {
      const data = await loadJson<{ contract: ScrapContractDetail }>(
        `/api/scrap-contract?id=${id}`,
        "A szerződés betöltése sikertelen.",
      );
      setDetail(data.contract);
    } catch (loadError) {
      setError(getErrorMessage(loadError, "A szerződés betöltése sikertelen."));
      setOpenContractId(null);
    }
  };

  const closePanel = () => {
    setOpenContractId(null);
    setDetail(null);
    setSelectedImageIds(new Set());
    setIsAddingItem(false);
    setNewItemSn("");
  };

  const addContract = async () => {
    const contractNumber = newContractNumber.trim();
    if (!contractNumber) return;

    setIsAdding(true);
    setError("");
    try {
      const response = await fetch("/api/scrap-contracts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contractNumber, recordDate: selectedDate }),
      });
      if (!response.ok) {
        setError(await readApiError(response, "A szerződés létrehozása sikertelen."));
        return;
      }

      const data = (await response.json()) as { contract: ScrapContractDetail };
      setNewContractNumber("");
      await Promise.all([loadCounts(), loadContracts(selectedDate)]);
      setOpenContractId(data.contract.id);
      setDetail(data.contract);
      setSelectedImageIds(new Set());
    } catch (addError) {
      setError(getErrorMessage(addError, "A szerződés létrehozása sikertelen."));
    } finally {
      setIsAdding(false);
    }
  };

  const addItem = async () => {
    const itemSn = newItemSn.trim();
    if (!itemSn || !openContractId) return;

    setIsAddingItemBusy(true);
    setError("");
    try {
      const response = await fetch(
        `/api/scrap-contract-items?contractId=${openContractId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ itemSn }),
        },
      );
      if (!response.ok) {
        setError(await readApiError(response, "Az Item/SN hozzáadása sikertelen."));
        return;
      }

      const data = (await response.json()) as { item: ScrapItem };
      setNewItemSn("");
      setIsAddingItem(false);
      setDetail((current) =>
        current ? { ...current, items: [data.item, ...current.items] } : current,
      );
      await Promise.all([loadCounts(), loadContracts(selectedDate)]);
    } catch (addError) {
      setError(getErrorMessage(addError, "Az Item/SN hozzáadása sikertelen."));
    } finally {
      setIsAddingItemBusy(false);
    }
  };

  const deleteItem = async (item: ScrapItem) => {
    setIsDeletingItem(true);
    setError("");
    try {
      const response = await fetch(`/api/scrap-contract-item?id=${item.id}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        setError(await readApiError(response, "Az Item/SN törlése sikertelen."));
        return;
      }

      setDetail((current) =>
        current
          ? { ...current, items: current.items.filter((row) => row.id !== item.id) }
          : current,
      );
      setSelectedImageIds((current) => {
        const next = new Set(current);
        item.images.forEach((image) => next.delete(image.id));
        return next;
      });
      await Promise.all([loadCounts(), loadContracts(selectedDate)]);
    } catch (deleteError) {
      setError(getErrorMessage(deleteError, "Az Item/SN törlése sikertelen."));
    } finally {
      setIsDeletingItem(false);
      setConfirmDeleteItem(null);
    }
  };

  const addPhotosToItem = async (itemId: number, event: ChangeEvent<HTMLInputElement>) => {
    // event.target.files is a *live* list tied to the input element —
    // resetting event.target.value clears it too, taking our reference
    // down with it. Snapshotting into a plain array first avoids that.
    const files = event.target.files ? Array.from(event.target.files) : [];
    event.target.value = "";
    if (files.length === 0) return;

    setUploadingItemId(itemId);
    setUploadFileProgress(`1/${files.length}`);
    setError("");
    try {
      // One request per photo — a single combined request full of raw,
      // uncompressed originals was hitting Cloudflare's own request size
      // and time limits once more than a handful of photos were involved.
      const uploadedImages: ScrapImage[] = [];
      for (let index = 0; index < files.length; index += 1) {
        setUploadFileProgress(`${index + 1}/${files.length}`);

        const form = new FormData();
        form.append("photos", files[index], files[index].name);

        const response = await fetch(`/api/scrap-contract-photos?id=${itemId}`, {
          method: "POST",
          body: form,
        });
        if (!response.ok) {
          setError(
            `${await readApiError(response, "A fotó feltöltése sikertelen.")} (${
              uploadedImages.length
            }/${files.length} már feltöltve)`,
          );
          return;
        }

        const data = (await response.json()) as { images: ScrapImage[] };
        uploadedImages.push(...data.images);
        setDetail((current) =>
          current
            ? {
                ...current,
                items: current.items.map((item) =>
                  item.id === itemId
                    ? { ...item, images: [...item.images, ...data.images] }
                    : item,
                ),
              }
            : current,
        );
      }

      await Promise.all([loadCounts(), loadContracts(selectedDate)]);
    } catch (uploadError) {
      setError(getErrorMessage(uploadError, "A fotó feltöltése sikertelen."));
    } finally {
      setUploadingItemId(null);
      setUploadFileProgress("");
    }
  };

  const toggleSelect = (imageId: number) => {
    setSelectedImageIds((current) => {
      const next = new Set(current);
      if (next.has(imageId)) next.delete(imageId);
      else next.add(imageId);
      return next;
    });
  };

  const selectAllInItem = (item: ScrapItem) => {
    setSelectedImageIds((current) => {
      const next = new Set(current);
      item.images.forEach((image) => next.add(image.id));
      return next;
    });
  };

  const clearSelectionInItem = (item: ScrapItem) => {
    setSelectedImageIds((current) => {
      const next = new Set(current);
      item.images.forEach((image) => next.delete(image.id));
      return next;
    });
  };

  const deleteSelected = async () => {
    if (selectedImageIds.size === 0 || !detail) return;
    setIsDeletingSelection(true);
    setError("");
    try {
      const ids = Array.from(selectedImageIds);
      const response = await fetch("/api/scrap-contract-images", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids }),
      });
      if (!response.ok) {
        setError(await readApiError(response, "A törlés sikertelen."));
        return;
      }

      setDetail((current) =>
        current
          ? {
              ...current,
              items: current.items.map((item) => ({
                ...item,
                images: item.images.filter((image) => !selectedImageIds.has(image.id)),
              })),
            }
          : current,
      );
      setSelectedImageIds(new Set());
      await Promise.all([loadCounts(), loadContracts(selectedDate)]);
    } catch (deleteError) {
      setError(getErrorMessage(deleteError, "A törlés sikertelen."));
    } finally {
      setIsDeletingSelection(false);
      setConfirmDeleteSelection(false);
    }
  };

  const saveAndClose = async () => {
    if (!openContractId || !detail) return;
    setIsSaving(true);
    setError("");
    try {
      // Every photo, across every item, is squeezed under the size limit
      // before the contract is sealed — except photos a previous SAVE
      // already compressed, which are skipped entirely so a re-opened
      // contract doesn't re-download and re-upload bytes that are already
      // done. The original is fetched back, compressed in the browser, and
      // the compressed copy replaces the original in place — which is also
      // what discards the original on the server.
      const allImages = detail.items
        .flatMap((item) => item.images)
        .filter((image) => !image.isCompressed);

      for (let index = 0; index < allImages.length; index += 1) {
        const image = allImages[index];
        setSaveProgress(`${index + 1}/${allImages.length}`);

        const original = await fetch(`/api/scrap-contract-image?id=${image.id}`, {
          cache: "no-store",
        });
        if (!original.ok) {
          setError(await readApiError(original, "A kép betöltése sikertelen."));
          return;
        }

        const originalFile = new File([await original.blob()], image.fileName, {
          type: image.contentType,
        });
        const compressed = await compressScrapPhoto(originalFile);
        if (compressed.error) {
          setError(compressed.error);
          return;
        }
        const thumbnail = await makeThumbnail(compressed.file);

        const form = new FormData();
        form.append("photo", compressed.file, compressed.file.name);
        if (thumbnail) form.append("thumbnail", thumbnail, thumbnail.name);
        const replaceResponse = await fetch(`/api/scrap-contract-image?id=${image.id}`, {
          method: "PUT",
          body: form,
        });
        if (!replaceResponse.ok) {
          setError(await readApiError(replaceResponse, "A kép tömörítése sikertelen."));
          return;
        }
      }

      const response = await fetch(`/api/scrap-contract?id=${openContractId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "done" }),
      });
      if (!response.ok) {
        setError(await readApiError(response, "A mentés sikertelen."));
        return;
      }

      closePanel();
      await Promise.all([loadCounts(), loadContracts(selectedDate)]);
    } catch (saveError) {
      setError(getErrorMessage(saveError, "A mentés sikertelen."));
    } finally {
      setIsSaving(false);
      setSaveProgress("");
    }
  };

  const downloadZip = async (contract: ScrapContract) => {
    setDownloadingId(contract.id);
    setError("");
    try {
      await downloadSelection(
        `/api/scrap-contracts/export?id=${contract.id}`,
        [],
        `${contract.contractNumber}.zip`,
      );
    } catch (downloadError) {
      setError(getErrorMessage(downloadError, "A letöltés sikertelen."));
    } finally {
      setDownloadingId(null);
    }
  };

  const downloadExcel = async (contract: ScrapContract) => {
    setDownloadingId(contract.id);
    setError("");
    try {
      await downloadSelection(
        `/api/scrap-contracts/export-xlsx?id=${contract.id}`,
        [],
        `${contract.contractNumber}.xlsx`,
      );
    } catch (downloadError) {
      setError(getErrorMessage(downloadError, "A letöltés sikertelen."));
    } finally {
      setDownloadingId(null);
    }
  };

  const deleteContract = async (contract: ScrapContract) => {
    setIsDeletingContract(true);
    setError("");
    try {
      const response = await fetch(`/api/scrap-contract?id=${contract.id}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        setError(await readApiError(response, "A szerződés törlése sikertelen."));
        return;
      }

      if (openContractId === contract.id) closePanel();
      await Promise.all([loadCounts(), loadContracts(selectedDate)]);
    } catch (deleteError) {
      setError(getErrorMessage(deleteError, "A szerződés törlése sikertelen."));
    } finally {
      setIsDeletingContract(false);
      setConfirmDeleteContract(null);
    }
  };

  /** A closed ("done") contract is reopened so its items/photos can be changed again. */
  const reopenContract = async (contract: ScrapContract) => {
    setReopeningId(contract.id);
    setError("");
    try {
      const response = await fetch(`/api/scrap-contract?id=${contract.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "open" }),
      });
      if (!response.ok) {
        setError(await readApiError(response, "A szerződés megnyitása sikertelen."));
        return;
      }

      await Promise.all([loadCounts(), loadContracts(selectedDate)]);
      await openContract(contract.id);
    } catch (reopenError) {
      setError(getErrorMessage(reopenError, "A szerződés megnyitása sikertelen."));
    } finally {
      setReopeningId(null);
    }
  };

  // One shared camera handler for the contract-number and item-SN scan
  // fields — the field name says which one a scan is for.
  const onCameraValue = useCallback((field: string, value: string) => {
    if (field === "newContractNumber") setNewContractNumber(value);
    if (field === "newItemSn") setNewItemSn(value);
  }, []);

  if (!isActive) return null;

  return (
    <CameraScanProvider formRef={formRef} onValue={onCameraValue}>
      <form
        ref={formRef}
        className="scrap-tab"
        onSubmit={(event) => event.preventDefault()}
      >
        {error && <p className="status-message is-error">{error}</p>}

        {!isAdding && openContractId === null && (
          <button
            className="create-task-button"
            type="button"
            onClick={() => setIsAdding(true)}
          >
            + ADD CONTRACT NUMBER
          </button>
        )}

        {isAdding && (
          <div className="scrap-add-row">
            <ScanField
              label="Contract number"
              name="newContractNumber"
              value={newContractNumber}
              onValue={(_field, value) => setNewContractNumber(value)}
            />
            <button
              className="save-button"
              type="button"
              disabled={!newContractNumber.trim()}
              onClick={() => void addContract()}
            >
              ADD
            </button>
            <button
              className="modify-button"
              type="button"
              onClick={() => {
                setIsAdding(false);
                setNewContractNumber("");
              }}
            >
              CANCEL
            </button>
          </div>
        )}

        <div className="scrap-contract-list">
          {contracts.length === 0 && (
            <p className="empty-state">No scrap contracts yet.</p>
          )}

          {contracts.map((contract) => {
            const isOpenPanel = openContractId === contract.id;
            const isDone = contract.status === "done";

            return (
              <div
                key={contract.id}
                className={`scrap-contract-card ${isDone ? "is-done" : "is-open"}`}
              >
                <button
                  type="button"
                  className="scrap-contract-header"
                  onClick={() => {
                    if (isOpenPanel) closePanel();
                    else if (!isDone) void openContract(contract.id);
                  }}
                  disabled={isDone}
                >
                  <div className="scrap-contract-header-top">
                    <span className="scrap-contract-number">{contract.contractNumber}</span>
                    <span className="scrap-contract-status">
                      {isDone ? "DONE" : "OPEN"}
                    </span>
                  </div>
                  <div className="scrap-contract-header-bottom">
                    <span className="scrap-contract-count">
                      {contract.itemCount} item{contract.itemCount === 1 ? "" : "s"} ·{" "}
                      {contract.photoCount} photo{contract.photoCount === 1 ? "" : "s"}
                    </span>
                    {isDone && contract.completedAt && (
                      <time className="scrap-contract-timestamp">
                        {formatTimestamp(contract.completedAt)}
                      </time>
                    )}
                  </div>
                </button>

                {isDone && (
                  <div className="scrap-download-actions">
                    <button
                      className="download-button"
                      type="button"
                      disabled={downloadingId === contract.id}
                      onClick={() => void downloadExcel(contract)}
                    >
                      EXCEL
                    </button>
                    <button
                      className="download-button"
                      type="button"
                      disabled={downloadingId === contract.id}
                      onClick={() => void downloadZip(contract)}
                    >
                      ZIP
                    </button>
                    <button
                      className="modify-button"
                      type="button"
                      disabled={reopeningId === contract.id}
                      onClick={() => void reopenContract(contract)}
                    >
                      {reopeningId === contract.id ? "OPENING…" : "MODIFY"}
                    </button>
                    <button
                      className="delete-button"
                      type="button"
                      onClick={() => setConfirmDeleteContract(contract)}
                    >
                      DELETE
                    </button>
                  </div>
                )}

                {!isDone && !isOpenPanel && (
                  <div className="scrap-download-actions">
                    <button
                      className="delete-button"
                      type="button"
                      onClick={() => setConfirmDeleteContract(contract)}
                    >
                      DELETE
                    </button>
                  </div>
                )}

                {isOpenPanel && detail && (
                  <div className="scrap-contract-panel">
                    <div className="scrap-panel-top-actions">
                      <button
                        className="save-button"
                        type="button"
                        disabled={isSaving || detail.items.length === 0}
                        onClick={() => void saveAndClose()}
                      >
                        {isSaving ? `COMPRESSING ${saveProgress}…` : "SAVE"}
                      </button>
                    </div>

                    <p className="photo-warning">
                      Photos are uploaded at full size. On SAVE, every photo is compressed
                      to JPEG, at most {SIZE_LIMIT_KB} KB — the original is discarded.
                    </p>

                    {!isAddingItem && (
                      <button
                        className="create-task-button"
                        type="button"
                        onClick={() => setIsAddingItem(true)}
                      >
                        + ADD ITEM/SN
                      </button>
                    )}

                    {isAddingItem && (
                      <div className="scrap-add-row">
                        <ScanField
                          label="Item / SN"
                          name="newItemSn"
                          value={newItemSn}
                          onValue={(_field, value) => setNewItemSn(value)}
                        />
                        <button
                          className="save-button"
                          type="button"
                          disabled={!newItemSn.trim() || isAddingItemBusy}
                          onClick={() => void addItem()}
                        >
                          ADD
                        </button>
                        <button
                          className="modify-button"
                          type="button"
                          onClick={() => {
                            setIsAddingItem(false);
                            setNewItemSn("");
                          }}
                        >
                          CANCEL
                        </button>
                      </div>
                    )}

                    <div className="scrap-item-list">
                      {detail.items.length === 0 && (
                        <p className="empty-state">No items yet — scan one in above.</p>
                      )}

                      {detail.items.map((item) => {
                        const selectedInItem = item.images.filter((image) =>
                          selectedImageIds.has(image.id),
                        ).length;

                        return (
                          <div className="scrap-item-card" key={item.id}>
                            <div className="scrap-item-header">
                              <span className="scrap-item-sn">{item.itemSn}</span>
                              <span className="scrap-item-count">
                                {item.images.length} photo
                                {item.images.length === 1 ? "" : "s"}
                              </span>
                              <button
                                className="delete-button"
                                type="button"
                                onClick={() => setConfirmDeleteItem(item)}
                              >
                                DELETE
                              </button>
                            </div>

                            {item.images.length > 0 && (
                              <div className="selection-toolbar">
                                <button
                                  type="button"
                                  className="modify-button"
                                  onClick={() => selectAllInItem(item)}
                                >
                                  SELECT ALL ({item.images.length})
                                </button>
                                {selectedInItem > 0 && (
                                  <>
                                    <button
                                      type="button"
                                      className="modify-button"
                                      onClick={() => clearSelectionInItem(item)}
                                    >
                                      CLEAR
                                    </button>
                                    <button
                                      type="button"
                                      className="delete-button"
                                      disabled={isDeletingSelection}
                                      onClick={() => setConfirmDeleteSelection(true)}
                                    >
                                      DELETE ({selectedInItem})
                                    </button>
                                  </>
                                )}
                              </div>
                            )}

                            <div className="scrap-photo-grid">
                              {item.images.map((image) => (
                                <label key={image.id} className="scrap-photo-tile">
                                  <input
                                    type="checkbox"
                                    checked={selectedImageIds.has(image.id)}
                                    onChange={() => toggleSelect(image.id)}
                                  />
                                  {image.isCompressed ? (
                                    <img
                                      className="scrap-photo-thumb"
                                      src={`/api/scrap-contract-image?id=${image.id}&thumb=1`}
                                      alt=""
                                      loading="lazy"
                                    />
                                  ) : (
                                    <span
                                      className="scrap-photo-thumb scrap-photo-thumb-placeholder"
                                      title="Preview loads after SAVE — the original is not downloaded early to save mobile data."
                                    >
                                      <svg aria-hidden="true" viewBox="0 0 24 24">
                                        <path d="M4 7h3l1.5-2h7L17 7h3a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1Z" />
                                        <circle cx="12" cy="13" r="3.2" />
                                      </svg>
                                    </span>
                                  )}
                                  <span className="scrap-photo-name">{image.fileName}</span>
                                  <span className="scrap-photo-size">
                                    {formatBytes(image.byteSize)}
                                  </span>
                                </label>
                              ))}
                            </div>

                            <label className="photo-input">
                              <span
                                className={
                                  uploadingItemId === item.id
                                    ? "photo-name"
                                    : "photo-placeholder"
                                }
                              >
                                {uploadingItemId === item.id
                                  ? `uploading ${uploadFileProgress}…`
                                  : "+ add photos"}
                              </span>
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                disabled={uploadingItemId === item.id}
                                onChange={(event) => void addPhotosToItem(item.id, event)}
                              />
                            </label>
                          </div>
                        );
                      })}
                    </div>

                    <div className="scrap-panel-actions">
                      <button
                        className="delete-button"
                        type="button"
                        onClick={() => setConfirmDeleteContract(contract)}
                      >
                        DELETE
                      </button>
                      <button className="modify-button" type="button" onClick={closePanel}>
                        BACK
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {confirmDeleteSelection && (
          <ConfirmDialog
            title="Delete selected photos?"
            message={`${selectedImageIds.size} photo${
              selectedImageIds.size === 1 ? "" : "s"
            } will be removed.`}
            confirmLabel="DELETE"
            isBusy={isDeletingSelection}
            onCancel={() => setConfirmDeleteSelection(false)}
            onConfirm={() => void deleteSelected()}
          />
        )}

        {confirmDeleteItem && (
          <ConfirmDialog
            title="Delete this Item/SN?"
            message={`"${confirmDeleteItem.itemSn}" and all ${
              confirmDeleteItem.images.length
            } of its photos will be permanently removed.`}
            confirmLabel="DELETE"
            isBusy={isDeletingItem}
            onCancel={() => setConfirmDeleteItem(null)}
            onConfirm={() => void deleteItem(confirmDeleteItem)}
          />
        )}

        {confirmDeleteContract && (
          <ConfirmDialog
            title="Delete this contract?"
            message={`"${confirmDeleteContract.contractNumber}" and all ${
              confirmDeleteContract.photoCount
            } of its photos will be permanently removed.`}
            confirmLabel="DELETE"
            isBusy={isDeletingContract}
            onCancel={() => setConfirmDeleteContract(null)}
            onConfirm={() => void deleteContract(confirmDeleteContract)}
          />
        )}
      </form>
    </CameraScanProvider>
  );
}
